<template>
  <section class="benefits-section">
    <div class="benefits-wrapper">

      <!-- Section Header -->
      <Motion
        :initial="{ opacity: 0, y: 30 }"
        :whileInView="{ opacity: 1, y: 0 }"
        :inViewOptions="{ once: true, margin: '-80px' }"
        :transition="{ duration: 0.7, ease: 'easeOut' }"
      >
        <div class="section-header">
          <h2 class="section-title">
            The <span class="gradient-text">Eduversity</span> Advantage
          </h2>
          <p class="section-subtitle">
            Four powerful pillars designed to transform students into industry-ready professionals and future entrepreneurs.
          </p>
        </div>
      </Motion>

      <!-- Benefit Cards -->
      <div class="benefits-grid">
        <Motion
          v-for="(card, i) in benefitCards"
          :key="i"
          :initial="{ opacity: 0, y: 40 }"
          :whileInView="{ opacity: 1, y: 0 }"
          :inViewOptions="{ once: true, margin: '-80px' }"
          :transition="{ duration: 0.7, delay: i * 0.12, ease: 'easeOut' }"
        >
          <article class="benefit-card" :style="{ '--accent': card.accent, '--accent-rgb': card.accentRgb }">
            <div class="benefit-top-bar" />

            <div class="benefit-header">
              <div class="benefit-icon-wrap">
                <span v-html="card.icon" />
              </div>
              <h3 class="benefit-title">{{ card.title }}</h3>
            </div>

            <ul class="benefit-list">
              <li v-for="(feat, j) in card.features" :key="j" class="benefit-item">
                <span class="benefit-check" v-html="iconCheck" />
                <span class="benefit-text">{{ feat }}</span>
              </li>
            </ul>
          </article>
        </Motion>
      </div>

      <!-- Animated Stats -->
      <div ref="statsRef" class="stats-row">
        <Motion
          v-for="(stat, i) in stats"
          :key="i"
          :initial="{ opacity: 0, y: 30, scale: 0.95 }"
          :whileInView="{ opacity: 1, y: 0, scale: 1 }"
          :inViewOptions="{ once: true, margin: '-60px' }"
          :transition="{ duration: 0.6, delay: i * 0.1, ease: 'easeOut' }"
        >
          <div class="stat-block">
            <span class="stat-value">{{ stat.format(displayCounts[i]) }}</span>
            <span class="stat-label">{{ stat.label }}</span>
          </div>
        </Motion>
      </div>

    </div>
  </section>
</template>

<script setup lang="ts">
// ── Icons ─────────────────────────────────────────────────────────────────────
const iconCheck = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`

const iconBriefcase = `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>`
const iconLightbulb = `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18h6"/><path d="M10 22h4"/><path d="M12 2a7 7 0 0 0-7 7c0 2.38 1.19 4.47 3 5.74V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.26c1.81-1.27 3-3.36 3-5.74a7 7 0 0 0-7-7z"/></svg>`
const iconRocket = `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg>`
const iconStartup = `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`

// ── Card Data ─────────────────────────────────────────────────────────────────
const benefitCards = [
  {
    title: 'Industry-Integrated Learning',
    accent: '#3b82f6', accentRgb: '59,130,246',
    icon: iconBriefcase,
    features: [
      '500+ hours of hands-on industry training',
      'Real-world projects from hiring partners',
      'Mentorship by active tech professionals',
      'Internship placement during degree program',
    ]
  },
  {
    title: 'Innovation & Competitions',
    accent: '#ff7800', accentRgb: '255,120,0',
    icon: iconLightbulb,
    features: [
      'Annual hackathons with cash prizes',
      'Idea pitch competitions for funding',
      'Research paper publication support',
      'Access to innovation labs and makerspaces',
    ]
  },
  {
    title: 'Career Acceleration',
    accent: '#10b981', accentRgb: '16,185,129',
    icon: iconRocket,
    features: [
      'Direct placement drives with top companies',
      '1:1 resume building and interview prep',
      'Average package higher than market standard',
      'Alumni network across 500+ hiring partners',
    ]
  },
  {
    title: 'Build Your Own Venture',
    accent: '#ffbb3f', accentRgb: '255,187,63',
    icon: iconStartup,
    features: [
      'Startup incubation and seed funding access',
      'Business model mentorship by founders',
      'IP filing and legal support for patents',
      'Investor pitch deck preparation',
    ]
  }
]

// ── Stats ─────────────────────────────────────────────────────────────────────
const stats = [
  { label: 'Worth Certificates', animTarget: 15, divisor: 10, format: (n: number) => `\u20B9${(n / 10).toFixed(1)}L+` },
  { label: 'Hackathons Hosted', animTarget: 8, divisor: 1, format: (n: number) => `${n}+` },
  { label: 'Students Trained', animTarget: 5200, divisor: 1, format: (n: number) => `${n.toLocaleString()}+` },
  { label: 'Industry Internship', animTarget: 1, divisor: 1, format: (n: number) => `${n} Year` },
]

const statsRef = ref<HTMLElement | null>(null)
const displayCounts = ref<number[]>(stats.map(() => 0))
const hasAnimated = ref(false)

function easeOutCubic(t: number) {
  return 1 - Math.pow(1 - t, 3)
}

function animateStat(index: number, target: number, duration: number) {
  const start = performance.now()

  function tick(now: number) {
    const elapsed = now - start
    const progress = Math.min(elapsed / duration, 1)
    const eased = easeOutCubic(progress)
    displayCounts.value[index] = Math.round(eased * target)
    if (progress < 1) requestAnimationFrame(tick)
  }
  requestAnimationFrame(tick)
}

onMounted(() => {
  if (!statsRef.value) return
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting && !hasAnimated.value) {
        hasAnimated.value = true
        stats.forEach((stat, i) => {
          animateStat(i, stat.animTarget, 1400)
        })
        observer.disconnect()
      }
    },
    { threshold: 0.3 }
  )
  observer.observe(statsRef.value)
})
</script>

<style scoped>
/* ── Section ───────────────────────────────────────────────────────────────── */
.benefits-section {
  padding: 4rem 2rem;
  position: relative;
  z-index: 10;
}

.benefits-wrapper {
  max-width: 1400px;
  margin: 0 auto;
}

/* ── Header ─────────────────────────────────────────────────────────────────── */
.section-header {
  text-align: center;
  margin-bottom: 4.5rem;
}

.section-title {
  font-size: clamp(2.2rem, 5.5vw, 4rem);
  font-weight: 800;
  color: var(--text-primary);
  margin: 0 0 1.25rem;
  letter-spacing: -0.02em;
  line-height: 1.15;
}

.section-subtitle {
  font-size: 1.1rem;
  color: var(--text-secondary);
  max-width: 680px;
  margin: 0 auto;
  line-height: 1.75;
}

.gradient-text {
  background: linear-gradient(135deg, #ff7800, #ffbb3f);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* ── Cards grid ───────────────────────────────────────────────────────────── */
.benefits-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 5rem;
}

/* ── Card ─────────────────────────────────────────────────────────────────── */
.benefit-card {
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 20px;
  overflow: hidden;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  transition:
    transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.32s ease,
    box-shadow 0.32s ease,
    background 0.3s ease;
}

[data-theme="light"] .benefit-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.benefit-card:hover {
  transform: translateY(-6px);
  border-color: rgba(var(--accent-rgb), 0.38);
  box-shadow:
    0 10px 46px rgba(var(--accent-rgb), 0.15),
    0 0 0 1px rgba(var(--accent-rgb), 0.1);
}

/* ── Accent bar ───────────────────────────────────────────────────────────── */
.benefit-top-bar {
  height: 3px;
  background: linear-gradient(90deg, var(--accent), rgba(var(--accent-rgb), 0.25));
  flex-shrink: 0;
}

/* ── Header ───────────────────────────────────────────────────────────────── */
.benefit-header {
  padding: 1.6rem 1.55rem 0.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.benefit-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(var(--accent-rgb), 0.14);
  border: 1px solid rgba(var(--accent-rgb), 0.28);
  color: var(--accent);
  flex-shrink: 0;
  transition: background 0.35s ease, border-color 0.35s ease;
}

.benefit-card:hover .benefit-icon-wrap {
  background: rgba(var(--accent-rgb), 0.22);
  border-color: rgba(var(--accent-rgb), 0.45);
}

.benefit-title {
  font-size: 1.08rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.35;
}

/* ── Feature list ─────────────────────────────────────────────────────────── */
.benefit-list {
  list-style: none;
  padding: 0 1.55rem 1.6rem;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.benefit-item {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  font-size: 0.88rem;
  color: var(--text-secondary);
  line-height: 1.6;
}

.benefit-check {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(var(--accent-rgb), 0.12);
  border: 1px solid rgba(var(--accent-rgb), 0.25);
  color: var(--accent);
  margin-top: 0.05rem;
  transition: background 0.3s ease, border-color 0.3s ease;
}

.benefit-card:hover .benefit-check {
  background: rgba(var(--accent-rgb), 0.2);
  border-color: rgba(var(--accent-rgb), 0.4);
}

/* ── Stats row ────────────────────────────────────────────────────────────── */
.stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  max-width: 1100px;
  margin: 0 auto;
}

.stat-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.4rem;
  padding: 1.8rem 1.2rem;
  background: var(--feature-bg);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  transition: background 0.3s ease, border-color 0.3s ease;
}

.stat-block:hover {
  background: var(--feature-hover-bg);
  border-color: var(--feature-hover-border);
}

.stat-value {
  font-size: clamp(1.6rem, 3.5vw, 2.4rem);
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.02em;
  background: linear-gradient(135deg, #ff7800, #ffbb3f);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.stat-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-secondary);
  letter-spacing: 0.02em;
}

/* ── Responsive ───────────────────────────────────────────────────────────── */
@media (max-width: 1100px) {
  .benefits-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .stats-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .benefits-section {
    padding: 5rem 1rem;
  }

  .benefits-grid {
    grid-template-columns: 1fr;
    gap: 1.1rem;
    margin-bottom: 3rem;
  }

  .stats-row {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .section-title {
    font-size: clamp(1.85rem, 7vw, 2.6rem);
  }
}
</style>
